import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Tabs,
  Typography,
  Badge,
  Descriptions,
  Collapse,
  Tag,
  Statistic,
  Row,
  Col,
  Button,
  ConfigProvider,
  Empty,
  TableColumnsType,
} from "antd";
import {
  CalendarOutlined,
  DollarOutlined,
  HomeOutlined,
  BankOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import {
  DonneesVisitesSpecifique,
  VisiteModel,
} from "../../../domaine/visiteModel";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { render } from "@testing-library/react";
// Assuming your interface is defined in a types file

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

interface VisiteHistoriqueProps {
  visites: VisiteModel[];
}

// Helper function to format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("fr-MA", {
    style: "currency",
    currency: "MAD",
  }).format(amount);
};

// Helper function to format date
const formatDate = (dateString: string | null): string => {
  if (!dateString) return "Non spécifié";

  return dateString;
};

const VisiteHistorique: React.FC<VisiteHistoriqueProps> = ({ visites }) => {
  // Add this state
  const [selectedVisite, setSelectedVisite] = useState<VisiteModel | null>(
    null
  );
  const [defaultActiveTabKey, setDefaultActiveTabKey] = useState("1");
  const { users } = useSelector((state: RootState) => state.user);
  //get one user by an id
  const getUserById = (id: any) => {
    return users.find((user: any) => user.id === id);
  };

  // Add this function to handle viewing details
  const handleViewDetails = (visite: VisiteModel) => {
    // Toggle selection: if same visite is clicked again, hide details
    setSelectedVisite((prevSelected) =>
      prevSelected && prevSelected.id === visite.id ? null : visite
    );
  };
  const getFinancialSummaryByType = (visite: VisiteModel, type: string) => {
    const specificData = visite.donnees_visites_specifiques.filter(
      (dvs: any) => dvs.programmation_type === type
    );

    if (specificData.length === 0) {
      return null;
    }

    // Calculate aggregate financial metrics
    const totalUnits = specificData.reduce(
      (sum: any, item: any) => sum + item.nbr_unites_total,
      0
    );
    const soldUnits = specificData.reduce(
      (sum: any, item: any) => sum + item.nbr_unites_vendues,
      0
    );
    const unsoldUnits = specificData.reduce(
      (sum: any, item: any) => sum + item.nbr_unites_invendues,
      0
    );

    // Find min and max prices across all items of this type
    const minPrice = Math.min(
      ...specificData.map((item: any) => item.prix_vente_min)
    );
    const maxPrice = Math.max(
      ...specificData.map((item: any) => item.prix_vente_max)
    );

    // Get details based on type
    const details = specificData.map((item: DonneesVisitesSpecifique) => {
      let detailInfo = null;

      if (type === "appartements" && item.fk_programmation_appartement_detail) {
        const detail = item.fk_programmation_appartement_detail;
        detailInfo = {
          name: detail.name,
          bedrooms: `${detail.nbr_de_chambres_min} - ${detail.nbr_de_chambres_max}`,
          bathrooms: `${detail.nbr_de_salles_de_bain_min} - ${detail.nbr_de_salles_de_bain_max}`,
          surface: `${detail.surface_min} - ${detail.surface_max} m²`,
          balcony: detail.balcon_terasse ? "Oui" : "Non",
          kitchen: detail.type_de_cuisine,
        };
      } else if (type === "villas" && item.fk_programmation_villas_detail) {
        const detail = item.fk_programmation_villas_detail;
        detailInfo = {
          name: detail.name,
          bedrooms: `${detail.nbr_de_chambres_min} - ${detail.nbr_de_chambres_max}`,
          bathrooms: `${detail.nbr_de_salles_de_bain_min} - ${detail.nbr_de_salles_de_bain_max}`,
          landSurface: `${detail.surface_terrain_min} - ${detail.surface_terrain_max} m²`,
          builtSurface: `${detail.surface_construite_min} - ${detail.surface_construite_max} m²`,
          basement: detail.sous_sol ? "Oui" : "Non",
          kitchen: detail.type_de_cuisine,
        };
      } else if (type === "bureaux" && item.fk_programmation_bureau_detail) {
        const detail = item.fk_programmation_bureau_detail;
        detailInfo = {
          name: detail.name,
          surface: `${detail.bureau_surface_min} - ${detail.bureau_surface_max} m²`,
          finish: detail.finition,
          cabling: detail.installation_cablage ? "Oui" : "Non",
        };
      } else if (type === "lc" && item.fk_programmation_lc_detail) {
        const detail = item.fk_programmation_lc_detail;
        detailInfo = {
          name: detail.name,
          rdc: `${detail.rdc_surface_min} - ${detail.rdc_surface_max} m²`,
          basement: detail.sous_sol ? "Oui" : "Non",
          basementSurface: detail.sous_sol
            ? `${detail.sous_sol_surface_min || 0} - ${
                detail.sous_sol_surface_max || 0
              } m²`
            : "N/A",
          mezzanine: detail.mezzanine ? "Oui" : "Non",
          mezzanineSurface: detail.mezzanine
            ? `${detail.mezzanine_surface_min || 0} - ${
                detail.mezzanine_surface_max || 0
              } m²`
            : "N/A",
          hsp: `${detail.hsp} m`,
        };
      }

      return {
        id: item.id,
        totalUnits: item.nbr_unites_total,
        soldUnits: item.nbr_unites_vendues,
        unsoldUnits: item.nbr_unites_invendues,
        minFullPrice: item.prix_vente_min,
        maxFullPrice: item.prix_vente_max,
        minUnitPrice: item.prix_vente_unitaire_min,
        maxUnitPrice: item.prix_vente_unitaire_max,
        minRentPrice: item.prix_loyer_min,
        maxRentPrice: item.prix_loyer_max,
        parkingIncluded: item.isPrixParkingInclus ? "Oui" : "Non",
        parkingPrice: item.prixParking,
        detail: detailInfo,
      };
    });

    return {
      totalUnits,
      soldUnits,
      unsoldUnits,
      soldPercentage:
        totalUnits > 0 ? ((soldUnits / totalUnits) * 100).toFixed(1) : "0",
      minPrice,
      maxPrice,
      details,
    };
  };

  const columns :TableColumnsType<VisiteModel> = [
    {
      title: "Date de visite",
      dataIndex: "date_visite",
      key: "date_visite",
      render: (date: string) => formatDate(date),
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),
    },
    {
      title: "Visiteur",
      dataIndex: "utilisateur", 
      key: "utilisateur", // Use the correct key
      render: (id: number) => {
        const user = getUserById(id);
        // Add a check to handle cases where user might not be found
        return user ? (
          <p>{user.prenom + " " + user.nom}</p>
        ) : (
          <p>Utilisateur inconnu</p> // Fallback display when user is not found
        );
      },
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),
    },
    {
      title: "Bureau de vente",
      dataIndex: "bureau_de_vente",
      key: "bureau_de_vente",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),
    },
    {
      title: "Contact",
      dataIndex: "contact_agent_commercial",
      key: "contact_agent_commercial",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),
    },
    {
      title: "État des ventes",
      dataIndex: "etat_des_ventes",
      key: "etat_des_ventes",
      render: (state: string) => {
        let color = "blue";
        if (state === "commercialisation non initiée") color = "processing";
        else if (state === "commercialisation achevée") color = "error";
        else if (state === "commercialisation initiée") color = "success";
        return <Tag color={color}>{state}</Tag>;
      },
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: VisiteModel) => (
        <Button type="primary" onClick={() => handleViewDetails(record)}>
          {record.id === selectedVisite?.id
            ? "Masquer détails"
            : "Voir détails"}
        </Button>
      ),
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),
    },
    // {
    //   title: 'Détails',
    //   key: 'details',
    //   render: (_:any, record: VisiteModel) => (
    //     <Collapse ghost>
    //       <Panel header="Voir détails" key="1">
    //         <Tabs defaultActiveKey="1">
    //           <TabPane
    //             tab={<span><HomeOutlined /> Appartements</span>}
    //             key="1"
    //             disabled={!getFinancialSummaryByType(record, 'appartements')}
    //           >
    //             {renderTypeDetails(record, 'appartements')}
    //           </TabPane>
    //           <TabPane
    //             tab={<span><HomeOutlined /> Villas</span>}
    //             key="2"
    //             disabled={!getFinancialSummaryByType(record, 'villas')}
    //           >
    //             {renderTypeDetails(record, 'villas')}
    //           </TabPane>
    //           <TabPane
    //             tab={<span><BankOutlined /> Bureaux</span>}
    //             key="3"
    //             disabled={!getFinancialSummaryByType(record, 'bureaux')}
    //           >
    //             {renderTypeDetails(record, 'bureaux')}
    //           </TabPane>
    //           <TabPane
    //             tab={<span><ShopOutlined /> Locaux Commerciaux</span>}
    //             key="4"
    //             disabled={!getFinancialSummaryByType(record, 'lc')}
    //           >
    //             {renderTypeDetails(record, 'lc')}
    //           </TabPane>
    //         </Tabs>
    //       </Panel>
    //     </Collapse>
    //   ),
    // },
  ];

  const renderTypeDetails = (visite: VisiteModel, type: string) => {
    const summary = getFinancialSummaryByType(visite, type);
    if (!summary) return <Text>Aucune donnée disponible</Text>;

    return (
      <>
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="Unités totales"
              value={summary.totalUnits}
              suffix={`unités`}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Unités vendues"
              value={summary.soldUnits}
              suffix={`(${summary.soldPercentage}%)`}
              valueStyle={{ color: "#3f8600" }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Unités invendues"
              value={summary.unsoldUnits}
              valueStyle={{ color: "#cf1322" }}
            />
          </Col>
        </Row>

        <Title level={5} style={{ marginTop: 20 }}>
          Détails des unités
        </Title>
        {summary.details.map((detail: any, index: any) => (
          <Card
            key={index}
            title={detail.detail?.name || `Type ${index + 1}`}
            style={{ marginBottom: 10 }}
            size="small"
          >
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Prix de vente"
                  value={`${formatCurrency(
                    detail.minFullPrice
                  )} - ${formatCurrency(detail.maxFullPrice)}`}
                  valueStyle={{ fontSize: "14px" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Prix de vente unitaire"
                  value={`${formatCurrency(
                    detail.minUnitPrice
                  )} - ${formatCurrency(detail.maxUnitPrice)}`}
                  valueStyle={{ fontSize: "14px" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Prix de loyer"
                  value={`${formatCurrency(
                    detail.minRentPrice
                  )} - ${formatCurrency(detail.maxRentPrice)}`}
                  valueStyle={{ fontSize: "14px" }}
                />
              </Col>
            </Row>

            {detail.detail && (
              <Descriptions
                layout="vertical"
                size="small"
                column={3}
                style={{ marginTop: 10 }}
              >
                <Descriptions.Item label="Parking inclus">
                  {detail.parkingIncluded}
                </Descriptions.Item>
                {detail.parkingPrice && (
                  <Descriptions.Item label="Prix parking">
                    {formatCurrency(detail.parkingPrice)}
                  </Descriptions.Item>
                )}
                {type === "appartements" && (
                  <>
                    <Descriptions.Item label="Chambres">
                      {detail.detail.bedrooms}
                    </Descriptions.Item>
                    <Descriptions.Item label="SdB">
                      {detail.detail.bathrooms}
                    </Descriptions.Item>
                    <Descriptions.Item label="Surface">
                      {detail.detail.surface}
                    </Descriptions.Item>
                    <Descriptions.Item label="Balcon">
                      {detail.detail.balcony}
                    </Descriptions.Item>
                    <Descriptions.Item label="Cuisine">
                      {detail.detail.kitchen}
                    </Descriptions.Item>
                  </>
                )}

                {type === "villas" && (
                  <>
                    <Descriptions.Item label="Chambres">
                      {detail.detail.bedrooms}
                    </Descriptions.Item>
                    <Descriptions.Item label="SdB">
                      {detail.detail.bathrooms}
                    </Descriptions.Item>
                    <Descriptions.Item label="Surface terrain">
                      {detail.detail.landSurface}
                    </Descriptions.Item>
                    <Descriptions.Item label="Surface construite">
                      {detail.detail.builtSurface}
                    </Descriptions.Item>
                    <Descriptions.Item label="Sous-sol">
                      {detail.detail.basement}
                    </Descriptions.Item>
                    <Descriptions.Item label="Cuisine">
                      {detail.detail.kitchen}
                    </Descriptions.Item>
                  </>
                )}

                {type === "bureaux" && (
                  <>
                    <Descriptions.Item label="Surface">
                      {detail.detail.surface}
                    </Descriptions.Item>
                    <Descriptions.Item label="Finition">
                      {detail.detail.finish}
                    </Descriptions.Item>
                    <Descriptions.Item label="Câblage">
                      {detail.detail.cabling}
                    </Descriptions.Item>
                  </>
                )}

                {type === "lc" && (
                  <>
                    <Descriptions.Item label="Surface RDC">
                      {detail.detail.rdc}
                    </Descriptions.Item>
                    <Descriptions.Item label="HSP">
                      {detail.detail.hsp}
                    </Descriptions.Item>
                    <Descriptions.Item label="Sous-sol">
                      {detail.detail.basement}
                    </Descriptions.Item>
                    {detail.detail.basement === "Oui" && (
                      <Descriptions.Item label="Surface sous-sol">
                        {detail.detail.basementSurface}
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Mezzanine">
                      {detail.detail.mezzanine}
                    </Descriptions.Item>
                    {detail.detail.mezzanine === "Oui" && (
                      <Descriptions.Item label="Surface mezzanine">
                        {detail.detail.mezzanineSurface}
                      </Descriptions.Item>
                    )}
                  </>
                )}
              </Descriptions>
            )}
          </Card>
        ))}
      </>
    );
  };

  const renderGeneralInfo = (visite: VisiteModel) => (
    <Card title="Informations générales" style={{ marginBottom: 20 }}>
      <Descriptions layout="vertical" size="small">
        <Descriptions.Item label="Date de visite" span={3}>
          <CalendarOutlined /> {formatDate(visite.date_visite)}
        </Descriptions.Item>
        <Descriptions.Item label="Bureau de vente">
          {visite.bureau_de_vente}
        </Descriptions.Item>
        <Descriptions.Item label="Contact">
          {visite.contact_agent_commercial}
        </Descriptions.Item>
        <Descriptions.Item label="État des ventes">
          <Badge
            status={
              visite.etat_des_ventes === "commercialisation non initiée"
                ? "processing"
                : visite.etat_des_ventes === "commercialisation initiée"
                ? "success"
                : visite.etat_des_ventes === "commercialisation achevée"
                ? "error"
                : "default"
            }
            text={visite.etat_des_ventes}
          />
        </Descriptions.Item>
        <Descriptions.Item label="Date de début commercialisation">
          {formatDate(visite.date_debut_commercialisation)}
        </Descriptions.Item>
        <Descriptions.Item label="Date de fin commercialisation">
          {formatDate(visite.date_fin_commercialisation)}
        </Descriptions.Item>
        <Descriptions.Item label="Date de livraison">
          {formatDate(visite.date_livraison)}
        </Descriptions.Item>
        <Descriptions.Item label="Livraison immédiate">
          {visite.livraison_immediate ? "Oui" : "Non"}
        </Descriptions.Item>
        <Descriptions.Item label="Pratique noire">
          {visite.pratique_noir ? "Oui" : "Non"}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );

  // Add this useEffect to dynamically set the default active tab
  useEffect(() => {
    if (selectedVisite) {
      // Determine which tab should be active by default
      if (getFinancialSummaryByType(selectedVisite, "appartements")) {
        setDefaultActiveTabKey("1"); 
      }
      if (getFinancialSummaryByType(selectedVisite, "villas")) {
        setDefaultActiveTabKey("2"); // Villas
      } else if (getFinancialSummaryByType(selectedVisite, "bureaux")) {
        setDefaultActiveTabKey("3"); // Bureaux
      } else if (getFinancialSummaryByType(selectedVisite, "lc")) {
        setDefaultActiveTabKey("4"); // Locaux Commerciaux
      } else {
        setDefaultActiveTabKey("1"); // Fallback to Appartements
      }
    }
  }, [selectedVisite]);

  const customizeRenderEmpty = () => (
    <div style={{ textAlign: 'center' }}>
<Empty description="Aucune visite disponible" />
    </div>
  );

  return (
    <div style={{ width: "100%", marginTop: "20px" }}>
      <Title level={4}>Historique des visites</Title>
      <ConfigProvider renderEmpty={customizeRenderEmpty}>
      <Table
        dataSource={visites}
        columns={columns}
        rowKey="id"
        size="small"
        style={{ width: "100%" }}
      />
      </ConfigProvider>
     
      {selectedVisite && (
        <div
          style={{
            marginTop: 20,
            padding: 16,
            border: "1px solid #f0f0f0",
            borderRadius: 2,
          }}
        >
          <Title level={4}>
            Détails de la visite: {formatDate(selectedVisite.date_visite)}
          </Title>
          {renderGeneralInfo(selectedVisite)}
          <Tabs activeKey={defaultActiveTabKey} onChange={setDefaultActiveTabKey}>
            <TabPane
              tab={
                <span>
                  <HomeOutlined /> Appartements
                </span>
              }
              key="1"
              disabled={
                !getFinancialSummaryByType(selectedVisite, "appartements")
              }
            >
              {renderTypeDetails(selectedVisite, "appartements")}
            </TabPane>
            <TabPane
              tab={
                <span>
                  <HomeOutlined /> Villas
                </span>
              }
              key="2"
              disabled={!getFinancialSummaryByType(selectedVisite, "villas")}
            >
              {renderTypeDetails(selectedVisite, "villas")}
            </TabPane>
            <TabPane
              tab={
                <span>
                  <BankOutlined /> Bureaux
                </span>
              }
              key="3"
              disabled={!getFinancialSummaryByType(selectedVisite, "bureaux")}
            >
              {renderTypeDetails(selectedVisite, "bureaux")}
            </TabPane>
            <TabPane
              tab={
                <span>
                  <ShopOutlined /> Locaux Commerciaux
                </span>
              }
              key="4"
              disabled={!getFinancialSummaryByType(selectedVisite, "lc")}
            >
              {renderTypeDetails(selectedVisite, "lc")}
            </TabPane>
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default VisiteHistorique;
